/**
 * CallStrip (#390 PR C polish) — compact in-game presentation of
 * Berta's status + last reply, designed to live alongside the
 * DominoGame overlay in the four supported viewport modes:
 *
 *   - tablet-landscape: side panel on the right (~30% of width)
 *   - tablet-portrait: top strip (~35% of height)
 *   - phone-landscape: same as tablet-portrait (top strip)
 *   - phone-portrait: 80px top pill, single horizontal row
 *
 * Renders three pieces of info — companion face indicator, status
 * text, last reply text — plus a visually-demoted "hang up" icon
 * (small, gray, no label) so the user has an escape hatch but isn't
 * tempted to tap it accidentally mid-game. The primary "leave the
 * game" action remains the big "Done" button in the DominoGame area.
 *
 * Props:
 *   status     — 'idle' | 'listening' | 'thinking' | 'speaking' | etc.
 *   reply      — Berta's last spoken text (string, may wrap)
 *   transcript — what was last heard from the user (optional; small)
 *   companionName — display name (default 'Lena')
 *   variant    — 'side' | 'strip' | 'pill' — drives layout
 *   onHangup   — callback when the small phone-down icon is tapped
 */
import React from 'react';
import { useLang } from '../i18n/LanguageContext';

const BRAND_ORANGE = '#E8845A';
// #441 Phase 1 — Gracefully read tablet strings from LanguageContext.
// CallStrip is rendered inside <LanguageProvider> at the App root,
// but tests and storybook callers may render it bare; if useLang()
// returns undefined we fall back to the static STATUS_LABEL et al.
function _useTabletStrings() {
  const ctx = useLang();
  return ctx && ctx.t && ctx.t.tablet ? ctx.t.tablet : null;
}
const IVORY = '#F8F0DC';

// Status → indicator color + emoji. Mirrors the larger CompanionFace
// palette so the in-game indicator and the full call view feel
// continuous to the user.
const STATUS_COLOURS = {
  idle: '#f97316',
  wake_listening: '#a8a29e',
  listening: '#ef4444',
  transcribing: '#8b5cf6',
  thinking: '#3b82f6',
  speaking: '#10b981',
  reconnecting: '#a8a29e',
  error: '#6b7280',
  quota: '#6b7280',
  // #430 — music_playing is a DISPLAY-only status. The state machine
  // stays at 'listening' or 'idle' under the hood; TabletApp swaps it
  // for this when nowPlaying is set, because the listening-resume
  // useEffect early-returns while music is active and the mic is in
  // fact closed. Without this swap the strip would render the red
  // "Listening" pulse while Berta literally can't hear the user.
  music_playing: '#a855f7',
  // #687 — wait mode. User asked Berta to be quiet for a side
  // conversation; the mic is still open (we're listening for the
  // wake-name) but Berta is producing no audio. Pale grey-blue —
  // signals "present but quiet", distinct from the red listening
  // pulse (which would lie — she IS listening, but won't respond
  // to anything except her name).
  waiting: '#94a3b8',
};

// #1089 — exported so the standardized ActivityFrame renders the same
// status icon + label as the in-call CallStrip (single source of truth
// for the state vocabulary; rule 9).
export const STATUS_EMOJI = {
  idle: '😊',
  wake_listening: '😌',
  listening: '👂',
  transcribing: '🤔',
  thinking: '💭',
  speaking: '🗣️',
  reconnecting: '⏳',
  error: '😔',
  quota: '😴',
  music_playing: '🎵',
  // #687 — finger-to-lips emoji conveys "quiet" without being
  // negative (sleeping/asleep would imply she's gone).
  waiting: '🤫',
};

export const STATUS_LABEL = {
  idle: '',
  wake_listening: 'Waking up',
  listening: 'Listening',
  // #407 — transcribing and thinking collapse into one user-facing
  // label ("Let me think") because the technical distinction between
  // STT-still-running and LLM-running doesn't matter to the user;
  // both mean "wait, she's working on it". The trailing animated dots
  // (ThinkingDots, below) carry the activity signal so the label
  // itself stays clean.
  transcribing: 'Let me think',
  thinking: 'Let me think',
  speaking: 'Speaking',
  reconnecting: 'Reconnecting',
  error: 'Something went wrong',
  quota: 'Resting for now',
  // #430 — copy reads as a status of HER, not a command for the user.
  // The dismiss control lives in the MusicView (#448) full-screen
  // overlay; the strip just truthfully signals that we're not
  // capturing audio right now.
  music_playing: 'Music is playing',
  // #687 — wait mode label. The hint ("say her name when you're
  // back") lives in the i18n table at `callstrip.hint.waiting`;
  // here we keep the bare English fallback short so an unknown
  // language degrades gracefully.
  waiting: 'Waiting',
};

// #430 — deliberately NOT pulsing. The pulse on listening/speaking
// communicates "live audio in flight"; for music_playing the audio
// is the YouTube track and we don't want to imply Berta-side
// activity that isn't happening.
const PULSE_STATUSES = new Set(['listening', 'speaking', 'wake_listening']);

// #407 — states where Berta is working but NOT listening to the user.
// The mic is closed during these (no audio captured) so we want a
// stronger visual cue: animated dots + muted-mic icon + the friendlier
// "Let me think" label all fire together.
const THINKING_STATUSES = new Set(['transcribing', 'thinking']);

export function isThinkingStatus(status) {
  return THINKING_STATUSES.has(status);
}


// ── Mini face indicator ────────────────────────────────────────────────


function MiniFace({ status, size }) {
  const color = STATUS_COLOURS[status] || STATUS_COLOURS.idle;
  const emoji = STATUS_EMOJI[status] || STATUS_EMOJI.idle;
  const isPulsing = PULSE_STATUSES.has(status);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.5),
        flexShrink: 0,
        boxShadow: isPulsing ? `0 0 0 6px ${color}40` : 'none',
        transition: 'all 0.3s ease',
        animation: isPulsing ? 'cs-pulse 1.5s infinite' : 'none',
      }}
      aria-label={STATUS_LABEL[status] || 'idle'}
    >
      {emoji}
    </div>
  );
}


// ── Connection-hiccup indicator (#381) ─────────────────────────────────


/**
 * Small amber pill shown next to the name when the server's empty-
 * transcript handler classified the most recent turn as a mechanical
 * failure (no audio frames / mic error / WS dropped) or a "VAD heard
 * speech but Deepgram returned empty" event. Auto-clears the moment a
 * successful transcript arrives back from the server (handled in
 * TabletApp's WS message handler — `connectionHiccup` is a parent prop).
 *
 * Designed to be subtle (Q2 → B in the design walkthrough): noticeable
 * to a family member or admin watching the screen, but not alarming
 * to an elderly user — they hear the apologetic re-prompt; the dot
 * is just supplementary signal.
 */
function ConnectionHiccupBadge({ hiccup }) {
  const ts = _useTabletStrings();
  if (!hiccup) return null;
  const label = hiccup.attempt >= 2
    ? ((ts && ts.callstrip && ts.callstrip.tryingAgain) || 'Trying again')
    : ((ts && ts.callstrip && ts.callstrip.hiccup) || 'Hiccup');
  // #551 — title (hover tooltip) now keyed; defensive English fallback.
  const tooltip = (ts && ts.callstrip && ts.callstrip.hiccupTooltip)
    || "Connection hiccup — the last turn didn't reach the server cleanly.";
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', marginLeft: 8,
        background: 'rgba(245, 158, 11, 0.85)',
        color: '#1f1607',
        borderRadius: 999,
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      <span style={{ fontSize: '0.85rem', lineHeight: 1 }} aria-hidden="true">📡</span>
      {label}
    </span>
  );
}


// ── Thinking dots (#407) ───────────────────────────────────────────────


/**
 * Animated 3-dot bouncer rendered next to the status label whenever
 * the call is in a thinking state. Provides the strongest "wait, I'm
 * working on it" signal short of full-screen overlay — designed to be
 * scannable in <2s by an elderly user looking elsewhere.
 *
 * Implementation: three dots, each with the same keyframe but staggered
 * animationDelay so the bounce travels left-to-right. SVG would also
 * work but plain divs keep the surface tiny + theme-able via parent CSS.
 */
function ThinkingDots({ size = 6, color = '#F8F0DC' }) {
  const dotStyle = {
    width: size, height: size, borderRadius: '50%',
    backgroundColor: color,
    display: 'inline-block',
    margin: `0 ${Math.round(size * 0.4)}px`,
    animation: 'cs-think-bounce 1.2s infinite ease-in-out both',
    opacity: 0.6,
  };
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex', alignItems: 'center',
        verticalAlign: 'middle', marginLeft: 6,
      }}
    >
      <span style={{ ...dotStyle, animationDelay: '-0.32s' }} />
      <span style={{ ...dotStyle, animationDelay: '-0.16s' }} />
      <span style={{ ...dotStyle }} />
    </span>
  );
}


// ── Muted-mic icon (#407) ──────────────────────────────────────────────


/**
 * Small crossed-microphone SVG. Rendered next to the status label
 * whenever the mic is currently muted (transcribing / thinking).
 * Tells the user "your voice isn't being captured right now — save
 * your breath until I'm done." aria-label so screen readers pick it up.
 */
function MutedMicIcon({ size = 16, color = '#F8F0DC' }) {
  // #551 — aria-label was hardcoded English. Now sourced from
  // tablet.callstrip.mutedMicLabel via the existing helper, with a
  // defensive English fallback for tests / bare renders.
  const ts = _useTabletStrings();
  const ariaLabel = (ts && ts.callstrip && ts.callstrip.mutedMicLabel) || 'microphone muted';
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
      role="img"
      style={{ verticalAlign: 'middle', marginRight: 6, opacity: 0.85, flexShrink: 0 }}
    >
      {/* Mic body — hollow rounded rect-on-stem, simplified */}
      <path
        d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"
        fill={color}
      />
      {/* Diagonal strikethrough (the universal "muted" mark) */}
      <line
        x1="3" y1="3" x2="21" y2="21"
        stroke={color} strokeWidth="2.4" strokeLinecap="round"
      />
    </svg>
  );
}


// ── You-said card (#432) ───────────────────────────────────────────────


/**
 * Strip-side "You said" card. Used by the default call view ONLY when
 * the focal area is consumed by a media activity (today: photos via
 * PhotoCarousel inline mode), so the user's last completed utterance
 * still has a visible home. Mirrors the YouSaidCard shape from #428
 * but slotted into the strip's typography.
 *
 * Returns null when there's no text — the card has no own purpose
 * without a transcript to show.
 */
function YouSaidStripCard({ text }) {
  // #551 — "You said" was a hardcoded English JSX text node here.
  // Now keyed under tablet.callstrip.youSaidLabel; defensive
  // English fallback when the LanguageProvider isn't in the tree.
  const ts = _useTabletStrings();
  const youSaidLabel = (ts && ts.callstrip && ts.callstrip.youSaidLabel) || 'You said';
  if (!text || !text.trim()) return null;
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.10)',
        border: '1px solid rgba(232,132,90,0.4)',
        borderRadius: 10,
        padding: '10px 12px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <div style={{
        fontSize: '0.65rem', fontWeight: 700,
        color: BRAND_ORANGE,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginBottom: 4,
      }}>
        {youSaidLabel}
      </div>
      <div style={{
        fontSize: '0.88rem',
        fontStyle: 'italic',
        color: IVORY,
        opacity: 0.95,
        lineHeight: 1.35,
        wordBreak: 'break-word',
      }}>
        &ldquo;{text}&rdquo;
      </div>
    </div>
  );
}


// ── Demoted hang-up icon ───────────────────────────────────────────────


// #431 — Phone-down icon now sits in a red disc instead of the
// translucent grey one from #405. Real-tablet testing showed the
// muted-grey treatment was too easy to miss even with the "End call"
// label. Size + label semantics from #405 Q3 are unchanged — only
// the colour gets bumped from "demoted grey" to "unmistakable red".
const HANGUP_RED = '#ef4444';
const HANGUP_RED_BORDER = '#dc2626';

function HangupIcon({ onClick, size = 36, showLabel = false }) {
  const ts = _useTabletStrings();
  const endCallText = (ts && ts.callstrip && ts.callstrip.endCall) || 'End call';
  const button = (
    <button
      onClick={onClick}
      aria-label={endCallText}
      title={endCallText}
      style={{
        background: HANGUP_RED,
        border: `1px solid ${HANGUP_RED_BORDER}`,
        borderRadius: '50%',
        width: Math.max(60, size + 24),
        height: Math.max(60, size + 24),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
        boxShadow: '0 2px 6px rgba(239, 68, 68, 0.35)',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Phone with downward tilt — universal "hang up" visual */}
        <path
          d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9a8.4 8.4 0 0 0-1.92 1.27c-.18.16-.43.27-.7.27-.28 0-.53-.11-.71-.29L1.39 12.85a.996.996 0 0 1 0-1.41C4.06 8.78 7.84 7 12 7s7.94 1.78 10.61 4.44c.18.18.29.43.29.71s-.11.53-.29.7l-2.12 2.12c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.27a8.42 8.42 0 0 0-1.92-1.27c-.33-.16-.56-.5-.56-.9v-3.1A14.59 14.59 0 0 0 12 9z"
          fill="#ffffff"
          transform="rotate(135 12 12)"
        />
      </svg>
    </button>
  );
  if (!showLabel) return button;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {button}
      <div style={{
        color: IVORY, opacity: 0.85, fontSize: '0.78rem', fontWeight: 600,
        letterSpacing: '0.02em',
      }}>
        {endCallText}
      </div>
    </div>
  );
}


// ── Pause / Resume button (#700) ───────────────────────────────────────


// #700 — Pause/resume button sits next to HangupIcon as a fallback for
// the voice sleep trigger. STT can mishear the companion name (Bas's
// 2026-05-14 staging repro: "Cindy" → "Sidney") and when it does the
// voice gate never fires, leaving the user with no working way to ask
// Berta to be quiet. The button is a deterministic control that
// doesn't depend on transcription accuracy.
//
// Visual demotion vs HangupIcon: same size, slate-grey (vs red) so the
// hang-up disc stays the most visually salient escape hatch in the
// strip. When `isWaiting` is true the button shows a play triangle
// and a green tint to signal "tap to resume"; otherwise a pause glyph
// in slate-grey.
const PAUSE_GREY = '#475569';
const PAUSE_GREY_BORDER = '#334155';
const RESUME_GREEN = '#10b981';
const RESUME_GREEN_BORDER = '#059669';

function PauseToggleIcon({ onClick, size = 36, isWaiting = false }) {
  const ts = _useTabletStrings();
  const pauseLabel = (ts && ts.callstrip && ts.callstrip.pauseLabel) || 'Be quiet';
  const resumeLabel = (ts && ts.callstrip && ts.callstrip.resumeLabel) || 'Resume';
  const label = isWaiting ? resumeLabel : pauseLabel;
  const bg = isWaiting ? RESUME_GREEN : PAUSE_GREY;
  const border = isWaiting ? RESUME_GREEN_BORDER : PAUSE_GREY_BORDER;
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '50%',
        width: Math.max(60, size + 24),
        height: Math.max(60, size + 24),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        flexShrink: 0,
        boxShadow: isWaiting
          ? '0 2px 6px rgba(16, 185, 129, 0.35)'
          : '0 2px 6px rgba(71, 85, 105, 0.35)',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {isWaiting ? (
          /* Play triangle — "resume listening" */
          <path d="M8 5v14l11-7z" fill="#ffffff" />
        ) : (
          /* Two vertical bars — universal pause glyph */
          <>
            <rect x="6" y="5" width="4" height="14" rx="1" fill="#ffffff" />
            <rect x="14" y="5" width="4" height="14" rx="1" fill="#ffffff" />
          </>
        )}
      </svg>
    </button>
  );
}


// ── Reply text (auto-truncate / scroll) ────────────────────────────────


function ReplyText({ reply, maxLines, fontSize }) {
  if (!reply) return null;
  return (
    <div
      style={{
        color: IVORY,
        fontSize,
        lineHeight: 1.35,
        opacity: 0.92,
        // Multi-line clamp so long replies don't push the layout
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
      }}
    >
      {reply}
    </div>
  );
}


/**
 * Transcript text — what Berta heard from the user. Visually
 * subordinate to the reply (italic, lighter, in quote marks) so the
 * primary visual is still Berta's reply, not the user's own words
 * echoed back. Bas's PR-#404 feedback: 'I would [like] to be able
 * to see the transcription of what I say there too.'
 *
 * #634 follow-up — the original implementation used `-webkit-line-clamp`
 * which truncates the END of the text after N lines. For a LIVE
 * partial transcript that's exactly backwards: the user is watching
 * the most recent words appear ("…twintig, eenentwintig, …") and
 * what got hidden was the OLDEST words that fired several seconds
 * ago — but line-clamp hid the LIVE tail with an ellipsis mid-word.
 *
 * The fix: anchor the text to the BOTTOM of a fixed-height box.
 * Overflow naturally happens at the TOP, so as the transcript grows
 * past the budget, the earliest words scroll off the top while the
 * most-recent words stay visible. A soft fade at the top hints that
 * older content was clipped, replacing the visual noise of "…".
 */
function TranscriptText({ transcript, maxLines, fontSize }) {
  if (!transcript) return null;
  const LINE_HEIGHT = 1.35;
  return (
    <div
      style={{
        // Fixed-height box sized to `maxLines * line-height` em
        // (the unit resolves against the inner div's font-size).
        // overflow:hidden + column-flex + justifyContent:flex-end
        // anchors the inner text to the bottom; top overflow is
        // clipped — exactly the right behaviour for a live preview.
        maxHeight: `${maxLines * LINE_HEIGHT}em`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        marginBottom: 4,
        // Soft fade at the top hints that older content was clipped.
        // Both vendor-prefixed because Safari is still on -webkit-.
        maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%)',
        WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 22%)',
      }}
    >
      <div
        style={{
          color: IVORY,
          fontSize,
          fontStyle: 'italic',
          lineHeight: LINE_HEIGHT,
          opacity: 0.65,
          wordBreak: 'break-word',
        }}
      >
        &ldquo;{transcript}&rdquo;
      </div>
    </div>
  );
}


// ── Variants ───────────────────────────────────────────────────────────


function SideVariant({ status, reply, transcript, companionName, onHangup, showHangupLabel, connectionHiccup, showReply, inlineYouSaid, onTogglePause, isWaiting, showTranscript = true }) {
  // Tablet landscape: vertical stack on the right side.
  //
  // #438 — every variable-width / variable-height element gets a
  // pinned container so the strip's overall frame stays stable as
  // status, reply, transcript and connection-hiccup-badge change.
  // Without this the elderly user sees the mini-face and hang-up
  // jump up/down between turns ("Listening" → "Music is playing"
  // re-flow, hiccup badge appearing, reply text growing in-game).
  // #441 Phase 1 — read all displayed strings from the tablet i18n
  // namespace so they localize to the elderly user's language. Falls
  // back to the static STATUS_LABEL etc. when the LanguageProvider
  // isn't in the tree (tests / storybook).
  const ts = _useTabletStrings();
  const statusText = (ts && ts.callstrip && ts.callstrip.status && ts.callstrip.status[status]) ?? STATUS_LABEL[status] ?? '';
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
        padding: '16px 12px',
        height: '100%',
        background: 'rgba(0,0,0,0.22)',
        borderLeft: `1px solid rgba(248,240,220,0.12)`,
        boxSizing: 'border-box',
      }}
    >
      {/* Name row: pinned single-line with the hiccup badge inline so
          it never wraps and pushes the mini-face down. Width 100% so
          alignment stays consistent. */}
      <div style={{
        // #852 — bumped from 0.85rem → 1.05rem so the companion name
        // is legible on the A9 production tablet at arm's length.
        color: IVORY, fontSize: '1.05rem', fontWeight: 600,
        letterSpacing: '0.06em', opacity: 0.7, textTransform: 'uppercase',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 4,
        flexShrink: 0,
        width: '100%',
        height: 28,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {companionName || 'Lena'}
        </span>
        <ConnectionHiccupBadge hiccup={connectionHiccup} />
      </div>
      {/* Mini-face — fixed-size already; flexShrink:0 belt-and-braces. */}
      <div style={{ flexShrink: 0 }}>
        <MiniFace status={status} size={96} />
      </div>
      {/* Status label: pinned to a fixed two-line equivalent height
          (~2.6em) so even the longest STATUS_LABEL value ("Music is
          playing") on a narrow tablet doesn't wrap and grow the row.
          nowrap + ellipsize on the inner span. The dots + muted-mic
          icon stay inline.
          #852 — bumped from 1.1rem → 1.35rem for tablet readability. */}
      <div style={{
        color: IVORY,
        fontSize: '1.35rem',
        fontWeight: 600,
        textAlign: 'center',
        height: 38,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}>
        {/* #407 — muted-mic icon precedes the label during thinking
            states so "your mic is off" lands first; dots trail the
            label so the activity signal animates beside the words. */}
        {isThinkingStatus(status) && <MutedMicIcon size={18} />}
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {statusText}
        </span>
        {isThinkingStatus(status) && <ThinkingDots size={7} />}
      </div>
      {/* Middle content area: flex:1 so it eats remaining space, with
          minHeight:0 + overflow:hidden so internal content (transcript /
          you-said card / in-game reply) clips inside this region without
          pushing the hang-up disc below the strip. */}
      <div style={{
        flex: '1 1 0',
        minHeight: 0,
        width: '100%',
        textAlign: 'center',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        {/* #432 — when a media overlay claims the focal area (today:
            photo carousel inline), the user's "You said" card moves
            here. Hides the small TranscriptText since the card is the
            same information at higher prominence. */}
        {inlineYouSaid ? (
          <YouSaidStripCard text={inlineYouSaid} />
        ) : (
          // #1032 — honour showTranscript here too (it used to be
          // StripVariant-only). The parent now passes
          // showTranscript={!!photoCarousel}, so in the side-by-side default
          // call view the live transcript lives in the focal BertaReplyPanel
          // (current-speaker slot), not the strip. The strip still shows it
          // during a photo carousel (via the inlineYouSaid card above).
          showTranscript && transcript && status !== 'idle' && (
            // #852 — fontSize 0.92rem → 1.15rem for A9 readability.
            <TranscriptText transcript={transcript} maxLines={3} fontSize="1.15rem" />
          )
        )}
        {/* #428 — reply now lives in the focal area for the default
            call view (BertaReplyPanel in TabletApp); CallStrip only
            renders it when the parent opts in (showReply=true).
            DominoGame still opts in because the focal area is the
            game board — strip is the only place to show Berta's text.
            #852 — fontSize 1rem → 1.2rem. */}
        {showReply && <ReplyText reply={reply} maxLines={6} fontSize="1.2rem" />}
      </div>
      {/* Hang-up + pause toggle at the bottom. Both pinned via
          flexShrink:0 so a long reply / transcript in the middle
          region can't squeeze them. #700 — pause sits to the LEFT of
          hangup so the hang-up disc remains the rightmost / most
          prominent escape.
          #700 follow-up — `alignItems: 'flex-start'` keeps the two
          discs' TOP edges aligned. When `showHangupLabel=true` the
          HangupIcon renders as a column (disc + "End call" text
          below), which is taller than the bare pause disc. With
          `alignItems: 'center'` the shorter pause button got centered
          vertically and visually floated higher than the hangup disc —
          Bas's 2026-05-14 staging feedback "the buttons should be
          aligned on the top". flex-start fixes that with no other
          layout impact. */}
      <div style={{
        paddingBottom: 4, flexShrink: 0,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        {onTogglePause && (
          <PauseToggleIcon onClick={onTogglePause} size={28} isWaiting={isWaiting} />
        )}
        <HangupIcon onClick={onHangup} size={32} showLabel={showHangupLabel} />
      </div>
    </div>
  );
}


function StripVariant({ status, reply, transcript, companionName, onHangup, showHangupLabel, connectionHiccup, showReply, onTogglePause, isWaiting, showTranscript = true }) {
  // Tablet portrait / phone landscape: horizontal strip across the top.
  const ts = _useTabletStrings();
  const statusText = (ts && ts.callstrip && ts.callstrip.status && ts.callstrip.status[status]) ?? STATUS_LABEL[status] ?? '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 14px',
        background: 'rgba(0,0,0,0.22)',
        borderBottom: `1px solid rgba(248,240,220,0.12)`,
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      {/* #852 — MiniFace 72 → 88 for tablet-portrait (A9) readability. */}
      <MiniFace status={status} size={88} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          // #852 — status row 0.75rem → 0.95rem. Tablet-portrait is
          // the A9-production layout; the name-and-status microcopy
          // was unreadable at 12 px on the device.
          color: IVORY, fontSize: '0.95rem', fontWeight: 600,
          letterSpacing: '0.06em', opacity: 0.65, textTransform: 'uppercase',
          display: 'inline-flex', alignItems: 'center', gap: 0,
        }}>
          {/* #407 — muted-mic icon precedes the label during thinking. */}
          {isThinkingStatus(status) && <MutedMicIcon size={16} />}
          {companionName || 'Lena'}
          {statusText && <> &mdash; {statusText}</>}
          {isThinkingStatus(status) && <ThinkingDots size={6} />}
          <ConnectionHiccupBadge hiccup={connectionHiccup} />
        </div>
        <div style={{ marginTop: 6 }}>
          {showTranscript && transcript && status !== 'idle' && (
            // #852 — fontSize 0.88rem → 1.3rem; maxLines 1 → 2 so the
            // user can actually read more than the first few words of
            // what's currently being said (per Story 1 acceptance
            // criterion: banner shows more than the first few words).
            // #1009 — `showTranscript` is false in the tablet-portrait
            // default call view, where the transcript moves to a
            // dedicated focal-area row (UserTranscriptRow). It stays
            // true for phone-landscape (same 'strip' variant) and during
            // a photo carousel, so both of those are unchanged.
            <TranscriptText transcript={transcript} maxLines={2} fontSize="1.3rem" />
          )}
          {/* #428 — see SideVariant comment.
              #852 — fontSize 1.02rem → 1.25rem; maxLines 2 → 3 to
              match the taller readable strip. */}
          {showReply && <ReplyText reply={reply} maxLines={3} fontSize="1.25rem" />}
        </div>
      </div>
      {/* #700 follow-up — sub-flex with `alignItems: 'flex-start'`
          so the pause disc and the (potentially label-decorated)
          hangup disc share a common TOP edge. The outer strip uses
          `alignItems: 'center'` so the MiniFace stays vertically
          centered; the buttons need their own context. */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        {onTogglePause && (
          <PauseToggleIcon onClick={onTogglePause} size={28} isWaiting={isWaiting} />
        )}
        <HangupIcon onClick={onHangup} size={32} showLabel={showHangupLabel} />
      </div>
    </div>
  );
}


function PillVariant({ status, reply, transcript, companionName, onHangup, connectionHiccup, showReply, onTogglePause, isWaiting, showTranscript = true }) {
  const ts = _useTabletStrings();
  const statusText = (ts && ts.callstrip && ts.callstrip.status && ts.callstrip.status[status]) ?? STATUS_LABEL[status] ?? '';
  // Phone portrait: compact 80px row. Vertical space is tight — show
  // either the user's transcript (while Berta is processing) OR
  // Berta's reply, not both. Transcript wins while non-idle, otherwise
  // reply takes over.
  // #1009 — `showTranscript` (prop) is false in the portrait default call
  // view, where the transcript moves to the focal-area UserTranscriptRow.
  // Default-true keeps every other caller unchanged.
  const pillTranscriptVisible = showTranscript && transcript && status !== 'idle' && (status === 'transcribing' || status === 'thinking');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.28)',
        borderBottom: `1px solid rgba(248,240,220,0.12)`,
        boxSizing: 'border-box',
        height: '100%',
      }}
    >
      {/* #852 — pill MiniFace 56 → 64 for a small readability bump. */}
      <MiniFace status={status} size={64} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          // #852 — pill label 0.7rem → 0.9rem (still constrained by
          // overall pill height; smaller bump than tablet variants).
          color: IVORY, fontSize: '0.9rem', fontWeight: 700,
          letterSpacing: '0.05em', opacity: 0.7, textTransform: 'uppercase',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          display: 'inline-flex', alignItems: 'center',
        }}>
          {/* #407 — pill is too tight for a separate muted-mic icon
              AND status text. Skip the icon here; dots still fit. */}
          {statusText || (companionName || 'Lena')}
          {isThinkingStatus(status) && <ThinkingDots size={4} />}
          <ConnectionHiccupBadge hiccup={connectionHiccup} />
        </div>
        {/* #428 — pill: when showReply is off (default call view) and
            we have a transcript, show that; otherwise nothing. The
            second-line area stays empty rather than being repurposed —
            keeps the pill height stable.
            #852 — transcript 0.85rem → 1.05rem; reply 0.92rem → 1.1rem. */}
        {pillTranscriptVisible ? (
          <TranscriptText transcript={transcript} maxLines={1} fontSize="1.05rem" />
        ) : showReply ? (
          <ReplyText reply={reply} maxLines={1} fontSize="1.1rem" />
        ) : null}
      </div>
      {onTogglePause && (
        <PauseToggleIcon onClick={onTogglePause} size={24} isWaiting={isWaiting} />
      )}
      <HangupIcon onClick={onHangup} size={28} />
    </div>
  );
}


// ── Public ─────────────────────────────────────────────────────────────


export default function CallStrip({
  status = 'idle',
  reply = '',
  transcript = '',
  companionName = 'Lena',
  variant = 'strip',
  onHangup = () => {},
  // #405 — when this strip is the sole exit (default call view, no
  // big "Done" button alongside) we want elderly users to always see
  // a paired "End call" text label next to the icon, not just a grey
  // disc. Off in the in-game CallStrip (where DominoGame's "Done"
  // button is the primary exit). Pill variant ignores this — strip
  // width is too tight for an inline label.
  showHangupLabel = false,
  // #381 — when set (truthy object {kind, attempt}), renders the
  // amber connection-hiccup pill in all three variants. Auto-clears
  // (parent setter) on the next successful transcript.
  connectionHiccup = null,
  // #428 — should the strip render Berta's reply text? Default-true
  // for back-compat (in-game DominoGame relies on it — focal area is
  // the game board, strip is the only place to show reply text).
  // The default call view in TabletApp passes false because the new
  // BertaReplyPanel owns the reply in the focal area.
  showReply = true,
  // #432 — when a media overlay (today: PhotoCarousel inline) takes
  // over the focal area, the focal-area YouSaidCard from #428 is
  // hidden. Pass the user's last completed utterance here and the
  // side variant renders a "You said" card in the strip instead.
  // Currently only honoured by SideVariant; other variants ignore.
  inlineYouSaid = '',
  // #700 — pause/resume button. When `onTogglePause` is provided,
  // each variant renders the PauseToggleIcon next to the hang-up
  // disc. `isWaiting` controls the icon's appearance (pause glyph
  // when False, play triangle + green tint when True). Falsy
  // `onTogglePause` (the back-compat default) hides the button —
  // tests and storybook callers that don't care can skip it.
  onTogglePause = null,
  isWaiting = false,
  // #1009 — in the tablet-portrait default call view the live transcript
  // moves out of the strip into a dedicated focal-area row
  // (UserTranscriptRow in TabletApp). The parent passes
  // showTranscript=false there so the strip stops rendering its inline
  // TranscriptText. Defaults true → unchanged for every other caller
  // (phone-landscape strip, in-game DominoGame strip, storybook/tests).
  // Honoured by StripVariant, PillVariant AND SideVariant (#1032 — side
  // now honours it too, so the side-by-side default call view can move the
  // live transcript into the focal BertaReplyPanel current-speaker slot).
  showTranscript = true,
}) {
  return (
    <>
      {/* Inline keyframes so the component is self-contained */}
      <style>{`
        @keyframes cs-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
        @keyframes cs-think-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40%           { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
      {variant === 'side' && (
        <SideVariant status={status} reply={reply} transcript={transcript}
                     companionName={companionName} onHangup={onHangup}
                     showHangupLabel={showHangupLabel}
                     connectionHiccup={connectionHiccup}
                     showReply={showReply}
                     inlineYouSaid={inlineYouSaid}
                     onTogglePause={onTogglePause}
                     isWaiting={isWaiting}
                     showTranscript={showTranscript} />
      )}
      {variant === 'strip' && (
        <StripVariant status={status} reply={reply} transcript={transcript}
                      companionName={companionName} onHangup={onHangup}
                      showHangupLabel={showHangupLabel}
                      connectionHiccup={connectionHiccup}
                      showReply={showReply}
                      onTogglePause={onTogglePause}
                      isWaiting={isWaiting}
                      showTranscript={showTranscript} />
      )}
      {variant === 'pill' && (
        <PillVariant status={status} reply={reply} transcript={transcript}
                     companionName={companionName} onHangup={onHangup}
                     connectionHiccup={connectionHiccup}
                     showReply={showReply}
                     onTogglePause={onTogglePause}
                     isWaiting={isWaiting}
                     showTranscript={showTranscript} />
      )}
    </>
  );
}
